import React, { useState, useEffect, useRef } from 'react';
import './Filters.css';

// Icons
const IconStar = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="#ccc" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor" />
  </svg>
);
const IconStarFilled = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="#f59e0b" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor" />
  </svg>
);
const IconClock = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <polyline points="12 6 12 12 16 14"></polyline>
  </svg>
);
const IconFire = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" fill="none" />
    <path d="M13.5 3C13.5 3 16.5 6.5 16.5 9.5C16.5 11.5 15 13 13.5 13C13.5 13 15.5 14.5 15.5 16.5C15.5 18.5 13.5 20.5 11.5 20.5C9.5 20.5 7.5 18.5 7.5 16.5C7.5 14.5 9.5 13 9.5 13C8 13 6.5 11.5 6.5 9.5C6.5 6.5 9.5 3 9.5 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const IconChevronDown = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#008080" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9"></polyline>
  </svg>
);
const IconChevronUp = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#008080" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="18 15 12 9 6 15"></polyline>
  </svg>
);

export default function Filters({ initial = {}, onChange = () => { }, categories = [], brands = [], specsMeta = [], enabledSections = {} }) {
  const sectionFlags = {
    showCategories: true,
    showFocus: true,
    showBrand: true,
    showPrice: true,
    showWarranty: true,
    showRating: true,
    showSpecs: true,
    showStock: true,
    ...enabledSections,
  };
  const [filters, setFilters] = useState({
    category: initial.category || '',
    category_child1: initial.category_child1 || '',
    category_child2: initial.category_child2 || '',
    offer: initial.offer || false,
    isNew: initial.isNew || false,
    specialPrice: initial.specialPrice || false,
    brand: initial.brand ? (initial.brand.includes(',') ? initial.brand.split(',') : [initial.brand]) : [],
    priceMin: initial.priceMin || '',
    priceMax: initial.priceMax || '',
    warranty: initial.warranty || '',
    ratingMin: initial.ratingMin || '',
  });

  const [brandSearch, setBrandSearch] = useState('');
  const [specs, setSpecs] = useState({});
  const [openSections, setOpenSections] = useState({
    categories: true,
    focus: true,
    brand: true,
    price: true,
    warranty: true,
    spec1: true,
    spec2: true,
    spec3: true,
    spec4: true,
    spec5: true,
    rating: true,
  });
  const prevDeepestRef = useRef('');
  const lastInitialKeyRef = useRef('');

  // Notify parent of changes
  const isMounted = useRef(false);

  useEffect(() => {
    // skip first run if it matches initial exactly? 
    // actually, best is to just not fire if nothing changed from what we were given.
    // However, simpler is to just skip the very first mount effect if we trust parent to load initial data.
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }

    const payload = { ...filters };

    // Convert brand array to comma-separated string if backend expects string
    if (Array.isArray(payload.brand)) {
      payload.brand = payload.brand.join(',');
    }

    // Deepest category logic
    const deepest = filters.category_child2 || filters.category_child1 || filters.category || '';
    payload.category = deepest;

    // Remove empty-string values so downstream builders don't receive '' as a value
    Object.keys(payload).forEach(k => {
      if (payload[k] === '') delete payload[k];
      // normalize brand: ensure empty string becomes absent, arrays remain
      if (k === 'brand' && payload[k] === '') delete payload[k];
    });

    // attach specs selections (if any)
    if (specs && typeof specs === 'object') {
      const specCopy = {};
      Object.entries(specs).forEach(([k, v]) => {
        if (!v) return;
        if (Array.isArray(v)) {
          const filtered = v.filter(x => x !== undefined && x !== null && String(x) !== '');
          if (filtered.length) specCopy[k] = filtered.slice();
        } else if (String(v) !== '') {
          specCopy[k] = v;
        }
      });
      if (Object.keys(specCopy).length) payload.specs = specCopy;
    }
    // emit payload to parent
    onChange(payload);

    // Track deepest category locally (useful for preventing duplicate work if needed)
    const deepestName = filters.category_child2_name || filters.category_child1_name || filters.categoryName || '';
    prevDeepestRef.current = deepestName || '';
  }, [filters, specs, onChange]);

  const activeCatRef = useRef(null);

  // Scroll active category into view when it changes
  useEffect(() => {
    if (activeCatRef.current) {
      activeCatRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [filters.category, filters.category_child1, filters.category_child2]);

  const toggleSection = (section) => {
    setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const updateFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const toggleBrand = (b) => {
    setFilters(prev => {
      const current = Array.isArray(prev.brand) ? prev.brand : (prev.brand ? [prev.brand] : []);
      if (current.includes(b)) {
        return { ...prev, brand: current.filter(item => item !== b) };
      } else {
        return { ...prev, brand: [...current, b] };
      }
    });
  };

  const handleResetFilters = () => {
    setFilters({
      category: '',
      category_child1: '',
      category_child2: '',
      categoryName: '',
      category_child1_name: '',
      category_child2_name: '',
      offer: false,
      isNew: false,
      specialPrice: false,
      brand: [],
      priceMin: '',
      priceMax: '',
      warranty: '',
      ratingMin: '',
    });
    setSpecs({});
    setBrandSearch('');
  };

  // Categories Logic
  const topCategories = categories || [];
  const nodeChildren = (node) => node.subcategories || node.children || [];

  // If the parent page provided an initial category (name or id), attempt to
  // resolve it against the provided `categories` tree and select the matching
  // node (top/child/grandchild). This ensures that navigating from header
  // links with `?category=Name` will result in the Filters UI showing that
  // category as selected.
  useEffect(() => {
    if (!initial) return;
    const next = {
      category: initial.category || '',
      category_child1: initial.category_child1 || '',
      category_child2: initial.category_child2 || '',
      offer: !!initial.offer,
      isNew: !!initial.isNew,
      specialPrice: !!initial.specialPrice,
      brand: initial.brand ? (Array.isArray(initial.brand) ? initial.brand : (String(initial.brand).includes(',') ? String(initial.brand).split(',').map(b => b.trim()).filter(Boolean) : [initial.brand])) : [],
      priceMin: initial.priceMin || '',
      priceMax: initial.priceMax || '',
      warranty: initial.warranty || '',
      ratingMin: initial.ratingMin || '',
      categoryName: initial.categoryName || '',
      category_child1_name: initial.category_child1_name || '',
      category_child2_name: initial.category_child2_name || ''
    };
    const key = JSON.stringify(next);
    if (key === lastInitialKeyRef.current) return;
    lastInitialKeyRef.current = key;
    setFilters(prev => ({ ...prev, ...next }));
  }, [initial]);

  useEffect(() => {
    if (!initial) return;
    const candidate = initial.category || initial.categoryName || '';
    if (!candidate) return;

    // normalize comparator
    const norm = (s) => (s === undefined || s === null) ? '' : String(s).toLowerCase().trim();
    const target = norm(candidate);

    if (!target) return;

    // search tree for a node with matching name or id
    const findPath = (nodes, tgt) => {
      for (const n of nodes || []) {
        if (!n) continue;
        const match = norm(n.name) === tgt || norm(n.id) === tgt;
        if (match) return [n];
        const children = nodeChildren(n);
        if (children && children.length) {
          for (const c of children) {
            if (!c) continue;
            const matchC = norm(c.name) === tgt || norm(c.id) === tgt;
            if (matchC) return [n, c];
            const gchildren = nodeChildren(c);
            if (gchildren && gchildren.length) {
              for (const g of gchildren) {
                if (!g) continue;
                const matchG = norm(g.name) === tgt || norm(g.id) === tgt;
                if (matchG) return [n, c, g];
              }
            }
          }
        }
      }
      return null;
    };

    const path = findPath(categories || [], target);
    if (path && path.length) {
      const l1 = path[0];
      const l2 = path[1] || '';
      const l3 = path[2] || '';
      setCategoryPath(l1, l2, l3);
    } else {
      // if candidate looks numeric, try to set by id directly
      const asNum = Number(candidate);
      if (!Number.isNaN(asNum) && Number.isFinite(asNum)) {
        setFilters(prev => {
          if (prev.category === String(asNum) && prev.category_child1 === '' && prev.category_child2 === '') return prev;
          return { ...prev, category: String(asNum), category_child1: '', category_child2: '' };
        });
      }
    }
  }, [initial, categories]);

  // Initialize specs selection from initial if provided
  useEffect(() => {
    if (initial && initial.specs && typeof initial.specs === 'object') {
      // sanitize initial.specs: remove empty arrays/values
      const clean = {};
      Object.entries(initial.specs).forEach(([k, v]) => {
        if (v === undefined || v === null) return;
        if (Array.isArray(v)) {
          const filtered = v.filter(x => x !== undefined && x !== null && String(x) !== '');
          if (filtered.length) clean[k] = filtered.slice();
        } else if (String(v) !== '') {
          clean[k] = v;
        }
      });
      setSpecs(clean);
    }
  }, [initial]);

  useEffect(() => {
    setFilters(prev => {
      const next = { ...prev };
      if (!sectionFlags.showBrand) next.brand = [];
      if (!sectionFlags.showPrice) { next.priceMin = ''; next.priceMax = ''; }
      if (!sectionFlags.showWarranty) next.warranty = '';
      if (!sectionFlags.showRating) next.ratingMin = '';
      if (!sectionFlags.showFocus) { next.offer = false; next.isNew = false; next.specialPrice = false; }
      return next;
    });
    if (!sectionFlags.showSpecs) {
      setSpecs({});
    }
  }, [sectionFlags.showBrand, sectionFlags.showPrice, sectionFlags.showWarranty, sectionFlags.showRating, sectionFlags.showFocus, sectionFlags.showSpecs]);

  // (dev) removed verbose console debug logging to keep console clean

  // Helper to set category path
  const setCategoryPath = (l1, l2 = '', l3 = '') => {
    setFilters(prev => {
      const nextCat = l1 ? String(l1.id || l1.name) : '';
      const nextC1 = l2 ? String(l2.id || l2.name) : '';
      const nextC2 = l3 ? String(l3.id || l3.name) : '';
      const nextName = l1 ? String(l1.name) : '';
      const nextC1Name = l2 ? String(l2.name) : '';
      const nextC2Name = l3 ? String(l3.name) : '';

      if (
        prev.category === nextCat &&
        prev.category_child1 === nextC1 &&
        prev.category_child2 === nextC2 &&
        prev.categoryName === nextName &&
        prev.category_child1_name === nextC1Name &&
        prev.category_child2_name === nextC2Name
      ) {
        return prev;
      }

      return {
        ...prev,
        category: nextCat,
        category_child1: nextC1,
        category_child2: nextC2,
        // also store human-readable names so client can send them directly
        categoryName: nextName,
        category_child1_name: nextC1Name,
        category_child2_name: nextC2Name
      };
    });
  };

  const handleCategoryClick = (l1, l2 = '', l3 = '') => {
    setSpecs({});
    setBrandSearch('');
    // Reset brand, price, warranty, rating and spec filters when category changes
    setFilters(prev => {
      const nextCat = l1 ? String(l1.id || l1.name) : '';
      const nextC1 = l2 ? String(l2.id || l2.name) : '';
      const nextC2 = l3 ? String(l3.id || l3.name) : '';
      const nextName = l1 ? String(l1.name) : '';
      const nextC1Name = l2 ? String(l2.name) : '';
      const nextC2Name = l3 ? String(l3.name) : '';

      return {
        ...prev,
        category: nextCat,
        category_child1: nextC1,
        category_child2: nextC2,
        categoryName: nextName,
        category_child1_name: nextC1Name,
        category_child2_name: nextC2Name,
        brand: [],
        priceMin: '',
        priceMax: '',
        warranty: '',
        ratingMin: '',
      };
    });
  };

  // Price Slider Logic (Simple visual representation)
  const handlePriceChange = (type, val) => {
    updateFilter(type, val);
  };

  // Mobile Open State
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="filters-sidebar">
      {/* Mobile Toggle Button */}
      <button
        className="mobile-filter-toggle"
        onClick={() => setMobileOpen(!mobileOpen)}
        aria-label={mobileOpen ? "Mbyll filtrat" : "Hap filtrat"}
      >
        <span>Filtrat</span>
        {mobileOpen ? <IconChevronUp /> : <IconChevronDown />}
      </button>

      <div className={`filters-body ${mobileOpen ? 'open' : ''}`}>

        {/* Categories Section */}
        {sectionFlags.showCategories ? (
          <>
            <div className="filter-section">
              <div className="filter-header" onClick={() => toggleSection('categories')}>
                <span className="section-title">Kategoritë</span>
                {openSections.categories ? <IconChevronUp /> : <IconChevronDown />}
              </div>
              {openSections.categories && (
                <div className="filter-content categories-list">
                  {topCategories.map(cat => {
                    const isSelected = filters.category === String(cat.id || cat.name);
                    const children = nodeChildren(cat);
                    return (
                      <div key={cat.id || cat.name} className="cat-item-l1" ref={isSelected ? activeCatRef : null}>
                        <div className={`cat-link ${isSelected ? 'active' : ''}`} onClick={() => handleCategoryClick(cat)}>
                          <div style={{ flex: 1 }}>{cat.name}</div>
                          {children.length > 0 && (
                            <span
                              style={{ fontSize: 10, color: isSelected ? '#008080' : '#999', padding: '0 4px', cursor: 'pointer' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (isSelected) {
                                  setFilters(prev => ({
                                    ...prev,
                                    category: '',
                                    category_child1: '',
                                    category_child2: '',
                                    categoryName: '',
                                    category_child1_name: '',
                                    category_child2_name: '',
                                    brand: [],
                                    priceMin: '',
                                    priceMax: '',
                                    warranty: '',
                                    ratingMin: '',
                                  }));
                                  setSpecs({});
                                  setBrandSearch('');
                                } else {
                                  handleCategoryClick(cat);
                                }
                              }}
                            >
                              {isSelected ? '▲' : '▼'}
                            </span>
                          )}
                        </div>
                        {children.length > 0 && isSelected && (
                          <div className="cat-children">
                            {children.map(child => {
                              const isChildSelected = filters.category_child1 === String(child.id || child.name);
                              const grandChildren = nodeChildren(child);
                              return (
                                <div key={child.id || child.name} className="cat-item-l2" ref={isChildSelected ? activeCatRef : null}>
                                  <div className={`cat-link ${isChildSelected ? 'active' : ''}`} onClick={(e) => { e.stopPropagation(); handleCategoryClick(cat, child); }}>
                                    <div style={{ flex: 1 }}>{child.name} {child.itemsCount ? `(${child.itemsCount})` : ''}</div>
                                  </div>
                                  {grandChildren.length > 0 && isChildSelected && (
                                    <div className="cat-grandchildren">
                                      {grandChildren.map(grand => (
                                        <div key={grand.id || grand.name}
                                          ref={filters.category_child2 === String(grand.id || grand.name) ? activeCatRef : null}
                                          className={`cat-link l3 ${filters.category_child2 === String(grand.id || grand.name) ? 'active' : ''}`}
                                          onClick={(e) => { e.stopPropagation(); handleCategoryClick(cat, child, grand); }}>
                                          {grand.name} {grand.itemsCount ? `(${grand.itemsCount})` : ''}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="divider"></div>
          </>
        ) : null}

        {/* In Focus Section */}
        {sectionFlags.showFocus ? (
          <>
            <div className="filter-section">
              <div className="filter-header" onClick={() => toggleSection('focus')}>
                <span className="section-title">Në fokus</span>
                {openSections.focus ? <IconChevronUp /> : <IconChevronDown />}
              </div>
              {openSections.focus && (
                <div className="filter-content focus-list">
                  <label className="checkbox-item">
                    <IconStar />
                    <span className="icon-label">E RE</span>
                    <input id="filter-isNew" name="filter-isNew" type="checkbox" checked={filters.isNew} onChange={e => updateFilter('isNew', e.target.checked)} />
                  </label>
                  <label className="checkbox-item">
                    <IconClock />
                    <span className="icon-label">OFERTË E LIMITUAR</span>
                    <input id="filter-offer" name="filter-offer" type="checkbox" checked={filters.offer} onChange={e => updateFilter('offer', e.target.checked)} />
                  </label>
                  <label className="checkbox-item">
                    <IconFire />
                    <span className="icon-label">ÇMIM SPECIAL</span>
                    <input id="filter-specialPrice" name="filter-specialPrice" type="checkbox" checked={filters.specialPrice} onChange={e => updateFilter('specialPrice', e.target.checked)} />
                  </label>
                </div>
              )}
            </div>

            <div className="divider"></div>
          </>
        ) : null}

        {/* Brand Section */}
        {sectionFlags.showBrand ? (
          <>
            <div className="filter-section">
              <div className="filter-header" onClick={() => toggleSection('brand')}>
                <span className="section-title">Marka</span>
                {openSections.brand ? <IconChevronUp /> : <IconChevronDown />}
              </div>
              {openSections.brand && (
                <div className="filter-content">
                  <input
                    id="brand-search"
                    name="brand-search"
                    type="text"
                    placeholder="Kërko markën"
                    className="brand-search"
                    value={brandSearch}
                    onChange={(e) => setBrandSearch(e.target.value)}
                  />
                  {/* compact: brands list will render below */}
                  <div className="brand-list">
                    {brands
                      .filter(b => b.toLowerCase().includes(brandSearch.toLowerCase()))
                      .map(b => (
                        <label key={b} className="checkbox-item">
                          <input
                            id={`brand-${b}`}
                            name={`brand-${b}`}
                            type="checkbox"
                            checked={(Array.isArray(filters.brand) ? filters.brand : [filters.brand]).includes(b)}
                            onChange={() => toggleBrand(b)}
                          />
                          <span className="brand-name">{b}</span>
                          {/* <span className="count">(96)</span> */}
                        </label>
                      ))}
                  </div>
                </div>
              )}
            </div>

            <div className="divider"></div>
          </>
        ) : null}

        {/* Price Section */}
        {sectionFlags.showPrice ? (
          <>
            <div className="filter-section">
              <div className="filter-header">
                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                  <span className="section-title">Nga</span> <span className="section-title">Deri në</span>
                </div>
              </div>
              <div className="price-inputs">
                <div className="price-input-wrap">
                  <input id="priceMin" name="priceMin" type="number" value={filters.priceMin} onChange={e => handlePriceChange('priceMin', e.target.value)} placeholder="15 000 L" />
                </div>
                <div className="price-input-wrap">
                  <input id="priceMax" name="priceMax" type="number" value={filters.priceMax} onChange={e => handlePriceChange('priceMax', e.target.value)} placeholder="217 000 L" />
                </div>
              </div>
              <div className="price-slider-container">
                <input
                  id="priceRangeMin"
                  name="priceRangeMin"
                  type="range"
                  min="0"
                  max="300000"
                  value={filters.priceMin || 0}
                  onChange={e => {
                    const val = Math.min(Number(e.target.value), (filters.priceMax || 300000) - 1000);
                    handlePriceChange('priceMin', val);
                  }}
                  className="thumb thumb-left"
                  style={{ zIndex: (filters.priceMin || 0) > 200000 ? 5 : 3 }}
                />
                <input
                  id="priceRangeMax"
                  name="priceRangeMax"
                  type="range"
                  min="0"
                  max="300000"
                  value={filters.priceMax || 300000}
                  onChange={e => {
                    const val = Math.max(Number(e.target.value), (filters.priceMin || 0) + 1000);
                    handlePriceChange('priceMax', val);
                  }}
                  className="thumb thumb-right"
                  style={{ zIndex: 4 }}
                />
                <div className="slider">
                  <div className="slider-track" />
                  <div
                    className="slider-range"
                    style={{
                      left: `${((filters.priceMin || 0) / 300000) * 100}%`,
                      right: `${100 - ((filters.priceMax || 300000) / 300000) * 100}%`
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="divider"></div>
          </>
        ) : null}

        {/* Warranty Section */}
        {sectionFlags.showWarranty ? (
          <>
            <div className="filter-section">
              <div className="filter-header" onClick={() => toggleSection('warranty')}>
                <span className="section-title">Garancia</span>
                {openSections.warranty ? <IconChevronUp /> : <IconChevronDown />}
              </div>
              {openSections.warranty && (
                <div className="filter-content">
                  {['12 muaj', '24 muaj', '36 muaj'].map(w => (
                    <label key={w} className="checkbox-item">
                      <input id={`warranty-${w}`} name={`warranty-${w}`} type="checkbox" checked={filters.warranty === w} onChange={() => updateFilter('warranty', filters.warranty === w ? '' : w)} />
                      <span>{w}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="divider"></div>
          </>
        ) : null}

        {/* Specs Sections (Top 5 from specsMeta) */}
        {sectionFlags.showSpecs ? (
          (specsMeta || []).map((s, idx) => (
            <React.Fragment key={s.key || idx}>
              <div className="filter-section">
                <div className="filter-header" onClick={() => toggleSection(`spec${idx}`)}>
                  <span className="section-title">{s.key}</span>
                  {openSections[`spec${idx}`] ? <IconChevronUp /> : <IconChevronDown />}
                </div>
                {openSections[`spec${idx}`] && (
                  <div className="filter-content">
                    {(Array.isArray(s.values) && s.values.length) ? (
                      <div>
                        <div className="spec-values-list">
                          {s.values.map(v => {
                            const sel = Array.isArray(specs[s.key]) ? specs[s.key] : (specs[s.key] ? [specs[s.key]] : []);
                            const checked = sel.includes(v);
                            return (
                              <label key={v} className="checkbox-item spec-item">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => {
                                    setSpecs(prev => {
                                      const cur = Array.isArray(prev[s.key]) ? prev[s.key].slice() : (prev[s.key] ? [prev[s.key]] : []);
                                      const exists = cur.includes(v);
                                      const next = exists ? cur.filter(x => x !== v) : [...cur, v];
                                      const out = { ...prev };
                                      if (Array.isArray(next) && next.length) out[s.key] = next;
                                      else delete out[s.key];
                                      return out;
                                    });
                                  }}
                                />
                                <span className="spec-value">{v}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <div className="spec-placeholder">No options available</div>
                    )}
                  </div>
                )}
              </div>
              <div className="divider"></div>
            </React.Fragment>
          ))
        ) : null}

        {/* Rating Section */}
        {sectionFlags.showRating ? (
          <div className="filter-section">
            <div className="filter-header" onClick={() => toggleSection('rating')}>
              <span className="section-title">Vlerësimi i klientit</span>
              {openSections.rating ? <IconChevronUp /> : <IconChevronDown />}
            </div>
            {openSections.rating && (
              <div className="filter-content rating-list">
                {[5, 4, 3, 2, 1].map(stars => (
                  <label key={stars} className="checkbox-item">
                    <input
                      id={`rating-${stars}`}
                      name={`rating-${stars}`}
                      type="checkbox"
                      checked={String(filters.ratingMin) === String(stars)}
                      onChange={() => updateFilter('ratingMin', String(filters.ratingMin) === String(stars) ? '' : String(stars))}
                    />
                    <div className="stars">
                      {[...Array(5)].map((_, i) => (
                        i < stars ? <IconStarFilled key={i} /> : <IconStar key={i} />
                      ))}
                    </div>
                    <span className="count">({Math.floor(Math.random() * 1000)})</span>
                  </label>
                ))}
                <label className="checkbox-item">
                  <input id="rating-all" name="rating-all" type="checkbox" checked={filters.ratingMin === ''} onChange={() => updateFilter('ratingMin', '')} />
                  <span>Të gjitha vlerësimet</span>
                </label>
              </div>
            )}
          </div>
        ) : null}

        {/* Reset Filters Button */}
        <div className="reset-filters-btn-container">
          <button 
            type="button" 
            className="reset-filters-btn" 
            onClick={handleResetFilters}
            aria-label="Pastro të gjithë filtrat"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6 }}>
              <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
              <path d="M3 3v5h5"></path>
            </svg>
            Pastro Filtrat
          </button>
        </div>

      </div> {/* End filters-body */}

    </div>
  );
}
